import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormArray, UntypedFormControl, Validators } from '@angular/forms';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, combineLatest, Observable, of, Subscription, tap } from 'rxjs';
import { map, startWith, switchMap, take } from 'rxjs/operators';

import { ElementActions, ElementTypes, GaContext, SelectOption } from '@fcom/common/interfaces';
import { finShare } from '@fcom/rx';
import { LanguageService } from '@fcom/ui-translate';
import {
  capitalize,
  EMAIL_REGEX,
  LATIN_ADDRESS_PATTERN,
  LATIN_ALPHA_PATTERN,
  PHONE_PATTERN,
  sortBy,
  startOf,
  TimeUnit,
  toISO8601,
  unsubscribe,
  ZIP_CODE_PATTERN,
} from '@fcom/core/utils';
import { TranslatedCountryCode } from '@fcom/common-booking/components/pax-details/pax-details-form-group.component';
import { ButtonIcon, ButtonTheme, ButtonType, IconPosition, NotificationTheme } from '@fcom/ui-components';
import { AnalyticsFormStatus, CheckboxOptionList, FormStatus, trackFormState } from '@fcom/service-forms';
import { HotjarCaptureEvent, HotjarService, ScrollService } from '@fcom/common/services';
import { GtmService } from '@fcom/common/gtm';
import {
  CorporateContact,
  EnrollCorporate,
  TravelAgency,
} from '@fcom/common/interfaces/corporate/enroll-new-corporate';
import {
  CorporateService,
  NAME_PATTERN,
  TERMS_AND_CONDITIONS_PREFIX,
} from '@fcom/common/services/corporate/corporate.service';
import { SentryLogger } from '@fcom/core/services';

import {
  CompanyDetailsForm,
  ContactDetailsForm,
  CountrySelectionForm,
  JoinForm,
  TravelAgencyDetailsForm,
} from './interfaces/form-interfaces';
import { asTypedFormGroup } from '../../utils';

const JOIN_CORPORATE_FORM_NAME = 'join-corporate-form';

export enum Page {
  CountrySelection = 'country-selection',
  CompanyInformation = 'company-information',
  ContactDetails = 'contact-persons-details',
  TravelAgencyDetails = 'travel-agency-information',
  Summary = 'review-and-submit',
  ThankYou = 'thank-you',
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'fin-join-corporate-form',
  styleUrls: ['./join-corporate-form.component.scss'],
  templateUrl: './join-corporate-form.component.html',
})
export class JoinCorporateFormComponent implements OnInit, OnDestroy {
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly NotificationTheme = NotificationTheme;
  readonly FormStatus = FormStatus;
  readonly Page = Page;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  readonly capitalize = capitalize;
  readonly asTypedFormGroup = asTypedFormGroup;
  hasRetried = false;

  @ViewChild('top')
  top: ElementRef;

  reactiveForm: FormGroup<JoinForm>;
  countrySelection: FormGroup<CountrySelectionForm>;
  companyDetails: FormGroup<CompanyDetailsForm>;
  contactDetails: FormGroup<ContactDetailsForm>;
  secondaryContactDetails: FormGroup<ContactDetailsForm>;
  travelAgencyDetails: FormGroup<TravelAgencyDetailsForm>;

  formBackup: FormGroup;
  formStatus: FormStatus;

  page = Page.CountrySelection;
  editMode = false;
  loading = false;
  submitIcon: ButtonIcon;

  countries$: Observable<TranslatedCountryCode[]>;
  countryOptions$: Observable<SelectOption[]>;
  phonePrefixes$: Observable<SelectOption[]>;
  languageOptions$: Observable<SelectOption[]>;
  bookingSystems: CheckboxOptionList;
  currencyOptions: SelectOption[];

  countryCodes: TranslatedCountryCode[];
  selectedCountryCode: TranslatedCountryCode;

  private subscriptions = new Subscription();
  termsLink$ = new BehaviorSubject<string | undefined>(undefined);
  returnLink$: Observable<string>;

  constructor(
    private fb: FormBuilder,
    private languageService: LanguageService,
    private corporateService: CorporateService,
    private scrollService: ScrollService,
    private gtmService: GtmService,
    private hotjarService: HotjarService,
    public changeDetectionRef: ChangeDetectorRef,
    private elementRef: ElementRef,
    public sentryLogger: SentryLogger
  ) {}

  ngOnInit(): void {
    this.setInputOptions();
    this.reactiveForm = this.createForm();
    this.formStatus = FormStatus.INITIAL;
    this.countrySelection = this.reactiveForm.get('countrySelection') as FormGroup<CountrySelectionForm>;
    this.companyDetails = this.reactiveForm.get('companyDetails') as FormGroup<CompanyDetailsForm>;
    this.contactDetails = this.reactiveForm.get('contactDetails') as FormGroup<ContactDetailsForm>;
    this.secondaryContactDetails = this.reactiveForm.get('secondaryContactDetails') as FormGroup<ContactDetailsForm>;
    this.travelAgencyDetails = this.reactiveForm.get('travelAgencyDetails') as FormGroup<TravelAgencyDetailsForm>;
    this.returnLink$ = this.corporateService.getReturnLink();
    this.addFormListeners();
    this.subscriptions.add(this.hotjarService.startCapture(HotjarCaptureEvent.CORPORATE_ENROLLMENT).subscribe());
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  changePage(page: Page, skipValidation = false): void {
    const pageForms = this.getPageForms();
    if (skipValidation || pageForms.every((form) => form.valid)) {
      this.page = page;
      this.scrollService.scrollToImmediate(this.top);
      this.trackState(`open-${page}`);
    } else {
      this.corporateService.scrollToInvalidField(pageForms, this.elementRef);
    }
  }

  previousPage(): void {
    const keys = Object.keys(Page);
    const currentPageNum = keys.findIndex((key) => Page[key] === this.page);
    const prevPage = keys[currentPageNum - 1];
    this.changePage(Page[prevPage]);
  }

  nextPage(): void {
    const keys = Object.keys(Page);
    const currentPageNum = keys.findIndex((key) => Page[key] === this.page);
    const nextPage = keys[currentPageNum + 1];
    this.changePage(Page[nextPage]);
  }

  modifyPage(page: Page): void {
    this.editMode = true;
    this.formStatus = FormStatus.INITIAL;
    this.formBackup = this.createForm();
    this.formBackup.patchValue(this.reactiveForm.value);
    this.changePage(page, true);
    this.trackState(`click-modify-${page}`);
  }

  cancel(): void {
    this.editMode = false;
    this.reactiveForm.patchValue(this.formBackup.value);
    this.changePage(Page.Summary);
  }
  handleError(err: unknown): void {
    if (err instanceof HttpErrorResponse && err.status === HttpStatusCode.PreconditionRequired) {
      this.formStatus = FormStatus.PRECONDITION_REQUIRED;
      trackFormState(JOIN_CORPORATE_FORM_NAME, AnalyticsFormStatus.PRECONDITION_REQUIRED, this.gtmService);
      this.sentryLogger.warn('Corporate contact form Akamai Crypto Challenge', {
        err,
      });
      this.setLoading(false);
      this.changeDetectionRef.markForCheck();
    } else {
      if (!this.hasRetried) {
        this.submitForm();
        this.hasRetried = true;
        return;
      }

      this.formStatus = FormStatus.ERROR;
      trackFormState(JOIN_CORPORATE_FORM_NAME, AnalyticsFormStatus.ERROR, this.gtmService);
      this.setLoading(false);
      this.changeDetectionRef.markForCheck();
    }
  }

  submitForm(): void {
    if (this.reactiveForm.invalid) {
      this.corporateService.scrollToInvalidField(this.reactiveForm, this.elementRef);
      return;
    }
    if (this.formStatus === FormStatus.LOADING) {
      return;
    }
    this.setLoading(true);
    this.formStatus = FormStatus.LOADING;
    trackFormState(JOIN_CORPORATE_FORM_NAME, AnalyticsFormStatus.SEND_ATTEMPT, this.gtmService);
    this.subscriptions.add(
      this.corporateService
        .enrollNewCorporate(this.buildPayload())
        .pipe(take(1))
        .subscribe({
          next: () => {
            trackFormState(JOIN_CORPORATE_FORM_NAME, AnalyticsFormStatus.SUCCESS, this.gtmService);
            this.formStatus = FormStatus.COMPLETE;
            this.changePage(Page.ThankYou);
          },
          error: (err: unknown) => {
            this.handleError(err);
          },
          complete: () => {
            this.setLoading(false);
            this.changeDetectionRef.markForCheck();
          },
        })
    );
  }

  countryChanged(countryCode: string): void {
    this.selectedCountryCode = this.countryCodes?.find((cc) => cc.countryCode === countryCode);
    const inputValue = `${this.selectedCountryCode?.countryCode}|${this.selectedCountryCode?.phonePrefix}`;
    this.contactDetails.get('phonePrefix').setValue(inputValue);
    this.secondaryContactDetails.get('phonePrefix').setValue(inputValue);
    this.termsLink$.next(TERMS_AND_CONDITIONS_PREFIX + this.corporateService.getCountryListForTerms()[countryCode]);
  }

  toggleOptionalForm(form: FormGroup, enabled: boolean, excludedKey: string): void {
    const controls = Object.keys(form.controls);
    controls
      .filter((key) => key !== excludedKey)
      .forEach((key) => {
        const control = form.get(key);
        enabled ? control.enable() : control.disable();
      });
  }

  getFormattedPhoneNumber(prefix: string, number: string | undefined): string {
    if (!number) {
      return '-';
    }
    return `+${prefix.split('|').pop()}${number}`;
  }

  setLoading(isLoading: boolean): void {
    this.loading = isLoading;
    this.submitIcon = isLoading
      ? {
          category: IconLibrary.SVG_LIBRARY,
          name: SvgLibraryIcon.TRANSFER_LOADER,
          position: IconPosition.RIGHT,
        }
      : null;
  }

  private trackState(event: string, elementType = ElementTypes.FORM): void {
    this.gtmService.trackElement(
      `join-corporate-${event}`,
      GaContext.SERVICE_FORMS,
      elementType,
      undefined,
      ElementActions.CLICK
    );
  }

  private getPageForms(): FormGroup[] {
    switch (this.page) {
      case Page.CountrySelection:
        return [this.countrySelection];
      case Page.CompanyInformation:
        return [this.companyDetails];
      case Page.ContactDetails:
        return [this.contactDetails, this.secondaryContactDetails];
      case Page.TravelAgencyDetails:
        return [this.travelAgencyDetails];
      case Page.Summary:
      default:
        return [this.reactiveForm];
    }
  }

  private createForm(): FormGroup {
    const bookingSystemControls = this.bookingSystems.map(() => new UntypedFormControl(false));

    return this.fb.group({
      countrySelection: this.fb.group({
        country: ['', Validators.required],
        noTravelAgency: ['', Validators.requiredTrue],
      }),
      companyDetails: this.fb.group({
        companyName: ['', Validators.compose([Validators.required, Validators.pattern(LATIN_ADDRESS_PATTERN)])],
        businessId: ['', Validators.required],
        address: ['', Validators.compose([Validators.required, Validators.pattern(LATIN_ADDRESS_PATTERN)])],
        postalCode: ['', Validators.compose([Validators.required, Validators.pattern(ZIP_CODE_PATTERN)])],
        city: ['', Validators.compose([Validators.required, Validators.pattern(NAME_PATTERN)])],
        language: ['', Validators.required],
        customerNumber: ['', Validators.pattern(/^[A-Za-z0-9]{8}$/)],
        budget: ['', Validators.pattern(/^[0-9]*$/)],
        currency: [this.currencyOptions[0].value],
      }),
      contactDetails: this.fb.group({
        firstName: ['', Validators.compose([Validators.required, Validators.pattern(NAME_PATTERN)])],
        familyName: ['', Validators.compose([Validators.required, Validators.pattern(NAME_PATTERN)])],
        phonePrefix: ['', Validators.required],
        phoneNumber: ['', Validators.compose([Validators.required, Validators.pattern(PHONE_PATTERN)])],
        email: ['', Validators.compose([Validators.required, Validators.pattern(EMAIL_REGEX)])],
        title: ['', Validators.compose([Validators.required, Validators.pattern(LATIN_ALPHA_PATTERN)])],
      }),
      secondaryContactDetails: this.fb.group({
        fillSecondaryContact: [],
        firstName: [
          { value: '', disabled: true },
          Validators.compose([Validators.required, Validators.pattern(NAME_PATTERN)]),
        ],
        familyName: [
          { value: '', disabled: true },
          Validators.compose([Validators.required, Validators.pattern(NAME_PATTERN)]),
        ],
        phonePrefix: [{ value: '', disabled: true }, Validators.required],
        phoneNumber: [
          { value: '', disabled: true },
          Validators.compose([Validators.required, Validators.pattern(PHONE_PATTERN)]),
        ],
        email: [
          { value: '', disabled: true },
          Validators.compose([Validators.required, Validators.pattern(EMAIL_REGEX)]),
        ],
        title: [
          { value: '', disabled: true },
          Validators.compose([Validators.required, Validators.pattern(LATIN_ALPHA_PATTERN)]),
        ],
      }),
      travelAgencyDetails: this.fb.group({
        fillTravelAgency: [''],
        name: [
          { value: '', disabled: true },
          Validators.compose([Validators.required, Validators.pattern(LATIN_ADDRESS_PATTERN)]),
        ],
        firstName: [
          { value: '', disabled: true },
          Validators.compose([Validators.required, Validators.pattern(NAME_PATTERN)]),
        ],
        familyName: [
          { value: '', disabled: true },
          Validators.compose([Validators.required, Validators.pattern(NAME_PATTERN)]),
        ],
        phonePrefix: [{ value: '', disabled: true }, Validators.required],
        phoneNumber: [
          { value: '', disabled: true },
          Validators.compose([Validators.required, Validators.pattern(PHONE_PATTERN)]),
        ],
        email: [
          { value: '', disabled: true },
          Validators.compose([Validators.required, Validators.pattern(EMAIL_REGEX)]),
        ],
        iataNumber: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(/^[0-9]{7,8}$/)]],
        officeCodes: [{ value: '', disabled: true }, Validators.required],
        pricingCode: [''],
        bookingSystems: new UntypedFormArray(bookingSystemControls),
      }),
      termsAndConditions: ['', Validators.requiredTrue],
    });
  }

  private getBookingSystems(): CheckboxOptionList {
    const availableOptions = [
      'abacus',
      'apollo',
      'amadeus',
      'axess',
      'eterm',
      'galileo',
      'ita',
      'sabre',
      'topas',
      'travelSky',
      'worldspan',
    ];
    return availableOptions.map((option) => ({
      id: option,
      label: `SERVICE_FORMS.serviceForms.joinCorporateForm.fields.bookingSystem.${option}`,
    }));
  }

  buildPayload(): EnrollCorporate {
    const previousCustomerNumber = this.companyDetails.get('customerNumber').value;
    const payload: EnrollCorporate = {
      companyName: this.companyDetails.get('companyName').value,
      companyTaxId: this.companyDetails.get('businessId').value,
      street: this.companyDetails.get('address').value,
      zipCode: this.companyDetails.get('postalCode').value,
      city: this.companyDetails.get('city').value,
      country: this.selectedCountryCode.countryCode,
      language: this.companyDetails.get('language').value,
      agreementStart: toISO8601(startOf(new Date(), TimeUnit.month)),
      travelBudget: this.companyDetails.get('budget').value,
      currency: this.companyDetails.get('currency').value,
      primaryContact: this.buildContactPayload(this.contactDetails),
      agreements: 'Y',
      termsAndCond: 'Y',
      additionalInfo: previousCustomerNumber
        ? `Previous Finnair Corporate number: ${previousCustomerNumber}`
        : undefined,
    };
    if (this.secondaryContactDetails.get('fillSecondaryContact').value) {
      payload.secondaryContact = this.buildContactPayload(this.secondaryContactDetails);
    }
    if (this.travelAgencyDetails.get('fillTravelAgency').value) {
      payload.travelAgents = this.buildTravelAgencyPayload(this.travelAgencyDetails);
    }
    return payload;
  }

  private buildContactPayload(form: FormGroup): CorporateContact {
    return {
      firstname: form.get('firstName').value,
      lastname: form.get('familyName').value,
      mobile: this.getFormattedPhoneNumber(form.get('phonePrefix').value, form.get('phoneNumber').value),
      email: form.get('email').value,
      jobTitle: form.get('title')?.value,
    };
  }

  private buildTravelAgencyPayload(form: FormGroup): TravelAgency[] {
    const controls = form.get('bookingSystems').value;
    const selectedBookingSystems = this.bookingSystems
      .filter((_val, index) => controls[index])
      .map((s) => s.id)
      .join(', ');
    return [
      {
        name: form.get('name').value,
        iata: form.get('iataNumber').value,
        officeCode: form.get('officeCodes').value,
        previousCorporateCodeFares: form.get('pricingCode').value,
        bookingSystem: selectedBookingSystems,
        firstname: form.get('firstName').value,
        lastName: form.get('familyName').value,
        mobile: this.getFormattedPhoneNumber(form.get('phonePrefix').value, form.get('phoneNumber').value),
        email: form.get('email').value,
      },
    ];
  }

  private addFormListeners(): void {
    this.subscriptions.add(
      this.travelAgencyDetails.get('fillTravelAgency').valueChanges.subscribe((checked) => {
        this.toggleOptionalForm(this.travelAgencyDetails, checked, 'fillTravelAgency');
        this.trackState('click-travel-agency-checkbox', ElementTypes.CHECKBOX);
      })
    );
    this.subscriptions.add(
      this.secondaryContactDetails.get('fillSecondaryContact').valueChanges.subscribe((checked) => {
        this.toggleOptionalForm(this.secondaryContactDetails, checked, 'fillSecondaryContact');
      })
    );
  }

  private setInputOptions(): void {
    this.countries$ = this.languageService.translate('countryCodes').pipe(finShare());
    this.countryOptions$ = this.getCountryOptions();
    this.phonePrefixes$ = this.getPhonePrefixes();
    this.languageOptions$ = this.getLanguageOptions();
    this.bookingSystems = this.getBookingSystems();
    this.currencyOptions = this.getCurrencyOptions();
    this.subscriptions.add(this.countries$.subscribe((countryCodes) => (this.countryCodes = countryCodes)));
  }

  private getCountryOptions(): Observable<SelectOption[]> {
    return this.countries$.pipe(
      map((countries) => {
        const corporateCountryKeys = Object.keys(this.corporateService.getCountryListForTerms());
        return corporateCountryKeys
          .map(
            (countryCode) =>
              ({
                value: countryCode,
                name: countries.find((cc) => cc.countryCode === countryCode)?.name,
              }) as SelectOption
          )
          .sort((locationA, locationB) => {
            return locationA.name > locationB.name ? 1 : -1;
          });
      })
    );
  }

  private getPhonePrefixes(): Observable<SelectOption[]> {
    return this.countries$.pipe(
      map((countryCodes: TranslatedCountryCode[]) => {
        const corporateCountryKeys = Object.keys(this.corporateService.getCountryListForTerms());
        const filteredCodes = countryCodes.filter((cc) => corporateCountryKeys.includes(cc.countryCode));
        return sortBy(
          filteredCodes.map((cc) => ({
            name: `${cc.name} (+${cc.phonePrefix})`,
            value: `${cc.countryCode}|${cc.phonePrefix}`,
          })),
          (option) => option.name
        );
      })
    );
  }

  private getCurrencyOptions(): SelectOption[] {
    return ['EUR', 'USD', 'SEK', 'NOK', 'DKK', 'GBP', 'CNY', 'HKD', 'JPY', 'KRW'].map((currency) => {
      return {
        name: currency,
        value: currency,
      };
    });
  }

  private getLanguageOptions(): Observable<SelectOption[]> {
    return this.languageService.translate('SERVICE_FORMS.serviceForms.joinCorporateForm.fields.language.options').pipe(
      switchMap((translations: { [key: string]: string }) => {
        const control = this.countrySelection.get('country');
        return combineLatest([of(translations), control.valueChanges.pipe(startWith(control.value))]);
      }),
      map(([translations, selectedCountry]) => {
        return Object.entries(translations)
          .filter(([language]) => {
            return language === 'en' || (selectedCountry === 'FI' && language === 'fi');
          })
          .map(([key, val]) => ({
            name: val,
            value: key,
          }));
      }),
      tap((values) => {
        // Preselect language if there is only one option
        if (values.length === 1) {
          this.companyDetails.get('language').setValue(values[0].value);
        }
      })
    );
  }
}
